import { useEffect, useState, type FC } from 'react';
import useSWR from 'swr';
import PricingInputField from '../PricingInputField/PricingInputField';
import { SWR_KEYS } from '../../../../../../../constants/constants';
import { countriesList, getCountriesOpenForSellingWhitProviders } from '../../../../../../../services/countriesList';
import { NudosDropdownWithSearchbar, NudosMaskedNumberInput } from '../../../../../../../components/NudosComponents';
import { ICountryDetail, ICountryExchangeDetail, IProviderDetails } from '../../../../../../../types/countries';
import { CloseNewCountryIcon } from '../../../../../../../assets/images/countryList';
import { Form } from 'antd';
import { roundUpNumber } from '../../../../../../../utils/formatNumbers';
import { INewCountryPricingFormData, IProductDetailId, PRODUCT_STATUS } from '../../../../../../../types/products';
import NudosButton from '../../../../../../../components/NudosComponents/NudosButton/NudosButton';
import ProductStatusTabs, { TProductStatus } from '../../../../../../../components/ProductStatusTabs/ProductStatusTabs';
import useOnlyDevelopers from '../../../../../../../state/setOnlyDevelopers';
import { resetNewCountryFields } from '../../../../../../../utils/pricing.utils';
import './PricingForm.scss';

interface PricingFormProps {
  setShowPricingForm?: (data: boolean) => void;
  setVerifyPricing: (areVerified: boolean) => void;
  availableProviders?: IProviderDetails[];
  productDataDetail?: IProductDetailId;
  showPricingForm: boolean;
  onAddCountryToPricing?: (formData: INewCountryPricingFormData, isQuoteProduct: boolean) => void;
}

const PricingForm: FC<PricingFormProps> = ({
  setShowPricingForm,
  setVerifyPricing,
  showPricingForm = false,
  productDataDetail,
  onAddCountryToPricing
}) => {
  const { isOnlyDevsAutomates } = useOnlyDevelopers();
  const { data: countriesListData } = useSWR([SWR_KEYS.countriesList], () => countriesList('buy&order=name'));
  const { data: countriesOpenForSellingWhitProvidersData } = useSWR(
    [SWR_KEYS.countriesOpenForSale],
    getCountriesOpenForSellingWhitProviders
  );

  const [currentCountry, setCurrentCountry] = useState<ICountryDetail[]>();
  const [isQuoteAvailable, setIsQuoteAvailable] = useState(false);
  const [isStoreAvailable, setIsStoreAvailable] = useState(false);
  const [productStatus, setProductStatus] = useState<TProductStatus>(PRODUCT_STATUS.OFF);
  const isCreatingProduct = !productDataDetail?.productId;

  const form = Form.useFormInstance();
  const { setFieldsValue } = form;
  const providerPrice = Form.useWatch(`providerPrice`);
  const margin = Form.useWatch(`margin`);
  const exchangeRate = Form.useWatch(`exchangeRate`);
  const taxRate = Form.useWatch(`taxRate`);
  const nudosPriceUsd = Form.useWatch(`nudosPriceUsd`);
  const nudosPriceUsdWithTaxes = Form.useWatch(`nudosPriceUsdWithTaxes`);
  const countryData = Form.useWatch(`countryData`);
  const [newProvider, setNewProvider] = useState<IProviderDetails>();
  const isMarginValid = !margin || Math.abs(margin) < 100;
  const isProviderPriceValid = !providerPrice || +providerPrice > 0;
  const isNudosPriceUsdWithTaxesInputValid = !nudosPriceUsdWithTaxes || +nudosPriceUsdWithTaxes > 0;
  const negativeOrZeroMarginAlert = !isMarginValid || (margin <= 0 && nudosPriceUsdWithTaxes) ? ' ' : undefined;
  const emptyFieldPlaceholder = '$0 USD';
  const renderCountryRequired = !productDataDetail?.productId
    ? true
    : !productDataDetail?.pricingData.length || showPricingForm;

  const pricingFields = [
    {
      componentSize: 'extraSmall',
      dataName: `taxRate`,
      label: 'Impuestos',
      initialValue: undefined,
      currentValue: taxRate,
      suffixSymbol: '%',
      placeholder: '%',
      isRequired: false,
      isDisabled: true
    },
    {
      componentSize: 'extraSmall',
      dataName: `exchangeRate`,
      label: 'Tasa de cambio',
      initialValue: undefined,
      currentValue: exchangeRate,
      suffixSymbol: countryData?.currencyCode ?? '',
      placeholder: `USD`,
      isRequired: false,
      isDisabled: true
    }
  ];
  const productPricingDataByCountry = productDataDetail?.pricingData;

  const filterRepeatedCountries = countriesListData?.filter(country => {
    return !productPricingDataByCountry?.some(product => product.nameCode === country.code);
  });

  const formDropdownOptionsFnProviders = (
    setCurrentOption: React.Dispatch<React.SetStateAction<IProviderDetails | undefined>>
  ) => {
    return (rawOptions: IProviderDetails[], clickOptionCallback: (option: IProviderDetails) => void) => {
      return rawOptions.map((option, i) => {
        const handleSelectThisOption = () => {
          setCurrentOption(option);
          setFieldsValue({
            ['providerId']: option?.id ?? ''
          });
          setFieldsValue({
            ['availableProviders']: rawOptions ?? ''
          });
          clickOptionCallback && clickOptionCallback(option);
        };
        return (
          <div className="optionContainer" key={`option-${option.id}-${i}`} onClick={handleSelectThisOption}>
            {option.name}
          </div>
        );
      });
    };
  };

  const formDropdownOptionsFnContries = (
    setCurrentOption: React.Dispatch<React.SetStateAction<ICountryDetail[] | undefined>>
  ) => {
    return (rawOptions: ICountryDetail[], clickOptionCallback: (option: ICountryDetail) => void) => {
      return rawOptions.map((option, i) => {
        const handleSelectThisOption = () => {
          setCurrentOption(option as ICountryDetail[]);
          setFieldsValue({
            ['newCountry']: option?.id ?? ''
          });

          setFieldsValue({
            ['countryData']: option ?? ''
          });
          setFieldsValue({
            ['exchangeRate']: option?.exchangeRate ? +option?.exchangeRate : 0
          });
          setFieldsValue({
            ['taxRate']: option?.taxRate ? +option?.taxRate : 0
          });

          clickOptionCallback && clickOptionCallback(option);
        };
        return (
          <div className="optionContainer" key={`option-${option.id}-${i}`} onClick={handleSelectThisOption}>
            {option.name}
          </div>
        );
      });
    };
  };

  const filterBySearchStringCallbackCountries = (search: string, rawOptions: ICountryDetail[]) => {
    return rawOptions.filter(taxData => taxData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()));
  };

  const filterBySearchStringCallbackProviders = (search: string, rawOptions: IProviderDetails[]) => {
    return rawOptions.filter(taxData => taxData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()));
  };

  const providersList = (code: string, countryProviders: ICountryExchangeDetail[]) => {
    if (code && countryProviders) {
      const myCountriesList = countryProviders.find(element => element.code === code);
      if (myCountriesList?.providers) {
        return myCountriesList.providers;
      }
      return [];
    }
    return [];
  };

  const getEcommercePriceSelectLabel = (includesTax: boolean) => {
    if (includesTax) {
      return <label className="ecommercePriceLabel labelSize">Precio con impuestos</label>;
    }
    return <label className="ecommercePriceLabel labelSize">Precio sin impues.</label>;
  };

  const calculateMarginProportion = (marginParam?: number) => {
    if (typeof marginParam !== 'number' || Math.abs(marginParam) >= 100) return;
    const getMarginProportion = marginParam / 100;
    const calculatedMarginFactor = 1 - getMarginProportion;
    return calculatedMarginFactor;
  };

  const calculateSellingPriceField = (margin: number, includesTax: boolean) => {
    const calculatedMargin = calculateMarginProportion(margin);
    const buyingTaxFactor = +taxRate / 100 + 1;
    const subtotal = calculatedMargin ? +providerPrice / calculatedMargin : 0;
    if (isNaN(subtotal)) return 0;
    const usdTotalPreTax = +(subtotal / exchangeRate);
    const usdTotalPlusTax = +(subtotal / exchangeRate) * buyingTaxFactor;
    if (!includesTax) return usdTotalPreTax;
    if (includesTax) return usdTotalPlusTax;
  };

  const calculateMarginField = (nudosPriceUsdWithTaxes: number) => {
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const providerPriceTimesTaxes = +providerPrice ? providerPrice * taxFactor : 0;
    const exchangeTimesTotal = +exchangeRate && +nudosPriceUsdWithTaxes ? exchangeRate * nudosPriceUsdWithTaxes : 0;
    const providerPriceToSellingPriceProportion = exchangeTimesTotal ? providerPriceTimesTaxes / exchangeTimesTotal : 1;
    const calculatedMargin = 1 - providerPriceToSellingPriceProportion;
    return calculatedMargin * 100;
  };

  const handleChangeMargin = (newMarginInput: string) => {
    setFieldsValue({
      [`margin`]: newMarginInput || 0
    });
    if (newMarginInput?.length < 1) {
      setFieldsValue({
        [`nudosPrice`]: 0
      });
      setFieldsValue({
        [`nudosPriceUsd`]: 0
      });
      setFieldsValue({
        [`nudosPriceWithTaxes`]: 0
      });
      setFieldsValue({
        [`nudosPriceUsdWithTaxes`]: 0
      });
      return;
    }
    const newMargin = isNaN(+newMarginInput) ? 0 : +newMarginInput;
    const newSellingPricePreTax = calculateSellingPriceField(newMargin, false);
    const newSellingPriceAfterTax = calculateSellingPriceField(newMargin, true);
    setFieldsValue({
      [`nudosPriceUsd`]: newSellingPricePreTax ? `${roundUpNumber(newSellingPricePreTax, 2)}` : 0
    });
    setFieldsValue({
      [`nudosPrice`]: newSellingPricePreTax ? `${roundUpNumber(newSellingPricePreTax * +exchangeRate, 2)}` : 0
    });
    setFieldsValue({
      [`nudosPriceUsdWithTaxes`]: newSellingPriceAfterTax ? `${roundUpNumber(newSellingPriceAfterTax, 2)}` : 0
    });
    setFieldsValue({
      [`nudosPriceWithTaxes`]: newSellingPriceAfterTax
        ? `${roundUpNumber(newSellingPriceAfterTax * +exchangeRate, 2)}`
        : 0
    });
  };

  const handleChangeNudosPriceUsdWithTaxes = (nudosPriceUsdWithTaxesInput: string) => {
    setFieldsValue({
      [`nudosPriceUsdWithTaxes`]: nudosPriceUsdWithTaxesInput || 0
    });
    if (nudosPriceUsdWithTaxesInput?.length < 1) {
      setFieldsValue({
        [`nudosPriceWithTaxes`]: 0
      });
      setFieldsValue({
        [`nudosPriceUsd`]: 0
      });
      setFieldsValue({
        [`nudosPrice`]: 0
      });
      setFieldsValue({
        [`margin`]: 0
      });
      return;
    }
    const sellingPriceNumber = nudosPriceUsdWithTaxesInput ? +nudosPriceUsdWithTaxesInput : 0;
    const calculatedMargin = calculateMarginField(sellingPriceNumber);
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const newPricePreTax = sellingPriceNumber ? sellingPriceNumber / taxFactor : 0;
    setFieldsValue({
      [`nudosPriceWithTaxes`]: sellingPriceNumber ? `${roundUpNumber(sellingPriceNumber * +exchangeRate, 2)}` : 0
    });
    setFieldsValue({
      [`nudosPriceUsd`]: newPricePreTax ? `${roundUpNumber(newPricePreTax, 2)}` : 0
    });
    setFieldsValue({
      [`nudosPrice`]: newPricePreTax && exchangeRate ? `${roundUpNumber(newPricePreTax * +exchangeRate, 2)}` : 0
    });
    setFieldsValue({
      [`margin`]: `${roundUpNumber(calculatedMargin, 4) || 0}`
    });
  };

  const handleChangeProviderPrice = (providerPriceInput: string) => {
    setFieldsValue({
      [`providerPrice`]: providerPriceInput || 0
    });
    handleChangeMargin(margin);
  };

  const handleSaveCountry = (isQuoteProduct: boolean) => {
    if (!onAddCountryToPricing || !countryData || !currentCountry) return;

    const formValues = {
      countryData,
      providerId: newProvider?.id,
      taxRate: taxRate,
      exchangeRate: exchangeRate,
      nudosPriceUsd: nudosPriceUsd,
      nudosPriceUsdWithTaxes: nudosPriceUsdWithTaxes,
      margin: margin,
      providerPrice: providerPrice
    };

    onAddCountryToPricing(formValues, isQuoteProduct);
    setVerifyPricing && setVerifyPricing(true);
    resetNewCountryFields(form);
    if (setShowPricingForm) {
      setShowPricingForm(false);
    }
  };

  /**
   * Cambia el estado en tienda elegido al crear el país y lo refleja en los campos del form.
   * "Prendido" requiere pricing completo y "Para cotizar" requiere país seleccionado; si no se
   * cumple, no cambia. El estado se aplica vía PATCH /store-status tras crear el producto.
   */
  const handleStatusChange = (newStatus: TProductStatus) => {
    if (newStatus === PRODUCT_STATUS.ON && !isStoreAvailable) return;
    if (newStatus === PRODUCT_STATUS.TO_QUOTE && !isQuoteAvailable) return;
    const statusConfig: Partial<Record<TProductStatus, { isActive: boolean; isQuoteProduct: boolean }>> = {
      [PRODUCT_STATUS.ON]: { isActive: true, isQuoteProduct: false },
      [PRODUCT_STATUS.TO_QUOTE]: { isActive: false, isQuoteProduct: true },
      [PRODUCT_STATUS.OFF]: { isActive: false, isQuoteProduct: false }
    };
    const config = statusConfig[newStatus];
    if (!config) return;
    setProductStatus(newStatus);
    setFieldsValue({ newCountryIsActive: config.isActive, newCountryIsQuoteProduct: config.isQuoteProduct });
  };

  /**
   * Calcula la disponibilidad de los estados en tienda y la validez del pricing.
   * Solo "Prendido" (PRODUCT_STATUS.ON) exige el pricing completo, porque publica el producto
   * con precio en tienda. "Para cotizar" y "Apagado" solo requieren un país seleccionado.
   */
  useEffect(() => {
    const pricingValuesAreValid = isMarginValid && isProviderPriceValid && isNudosPriceUsdWithTaxesInputValid;
    const hasSelectedCountry = !!countryData && !!currentCountry;
    setIsQuoteAvailable(hasSelectedCountry);

    const hasRequiredFields =
      hasSelectedCountry && !!margin && !!providerPrice && !!nudosPriceUsdWithTaxes && pricingValuesAreValid;
    setIsStoreAvailable(hasRequiredFields);

    const isVerified =
      productStatus === PRODUCT_STATUS.ON
        ? hasRequiredFields && pricingValuesAreValid && typeof taxRate === 'number' && typeof exchangeRate === 'number'
        : hasSelectedCountry;

    setVerifyPricing && setVerifyPricing(showPricingForm ? isVerified : true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [margin, providerPrice, taxRate, exchangeRate, setVerifyPricing, currentCountry, productStatus, countryData, nudosPriceUsdWithTaxes]);

  useEffect(() => {
    if (isCreatingProduct) {
      setFieldsValue({ newCountryIsActive: false, newCountryIsQuoteProduct: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pricingForm">
      <div className="pricingFormContainer">
        <Form.Item name="newCountry" required={renderCountryRequired}>
          <NudosDropdownWithSearchbar
            label="País"
            componentSize="mediumSmall"
            customClassLabel="customClassLabel"
            formDropdownOptionsCallback={formDropdownOptionsFnContries(setCurrentCountry)}
            filterBySearchStringCallback={filterBySearchStringCallbackCountries}
            customPlaceholder="Selecciona el país"
            currentValue={currentCountry as ICountryDetail}
            isFilled={!!currentCountry}
            rawOptions={(filterRepeatedCountries as ICountryDetail[]) ?? []}
          />
        </Form.Item>
        <NudosMaskedNumberInput
          componentSize="small"
          label={`Costo de compra`}
          placeholder={`$0`}
          currentValue={providerPrice}
          handleChange={inputText => handleChangeProviderPrice(inputText || '')}
          maskPrefix={'$'}
          maskSufix={`${countryData?.currencyCode}`}
          roundToClosest
          numberOfDecimals={2}
          errorText={!isProviderPriceValid ? ' ' : undefined}
        />
        <NudosMaskedNumberInput
          label={`Margen`}
          componentSize="extraSmall"
          placeholder={'0 %'}
          currentValue={`${margin || ''}`}
          handleChange={inputText => handleChangeMargin(inputText || '')}
          maskPrefix={''}
          maskSufix={'%'}
          roundToClosest
          numberOfDecimals={4}
          errorText={negativeOrZeroMarginAlert}
          inputPadding={12}
        />
        {pricingFields.map(field => {
          return (
            <PricingInputField
              key={field.dataName}
              componentSize={field.componentSize}
              dataName={field.dataName}
              label={field.label}
              initialValue={typeof field.initialValue === 'number' ? field.initialValue : 0}
              currentValue={typeof field.currentValue === 'number' ? field.currentValue : 0}
              suffixSymbol={field.suffixSymbol}
              placeholder={field.placeholder}
              isRequired={field.isRequired}
              isDisabled={field.isDisabled}
            />
          );
        })}
        <NudosMaskedNumberInput
          componentSize="small"
          label={getEcommercePriceSelectLabel(false)}
          placeholder={emptyFieldPlaceholder}
          currentValue={nudosPriceUsd}
          maskPrefix={'$'}
          maskSufix={'USD'}
          roundToClosest
          numberOfDecimals={2}
          isDeactivated={true}
        />
        <NudosMaskedNumberInput
          componentSize="small"
          label={getEcommercePriceSelectLabel(true)}
          placeholder={emptyFieldPlaceholder}
          isDeactivated={false}
          handleChange={inputText => handleChangeNudosPriceUsdWithTaxes(inputText || '')}
          currentValue={nudosPriceUsdWithTaxes}
          maskPrefix={'$'}
          maskSufix={' USD'}
          roundToClosest
          numberOfDecimals={2}
          errorText={!isNudosPriceUsdWithTaxesInputValid ? ' ' : undefined}
        />

        <NudosDropdownWithSearchbar
          label="Proveedor"
          componentSize="mediumSmall"
          customClassLabel="customClassLabel"
          formDropdownOptionsCallback={formDropdownOptionsFnProviders(setNewProvider)}
          filterBySearchStringCallback={filterBySearchStringCallbackProviders}
          customPlaceholder="Proveedor"
          currentValue={newProvider}
          isFilled={!!newProvider}
          rawOptions={
            providersList(countryData?.code, countriesOpenForSellingWhitProvidersData as ICountryExchangeDetail[]) || []
          }
        />
        <Form.Item name="countryData" hidden required={renderCountryRequired} />
        <Form.Item name={`isActive`} hidden />
        <Form.Item name={`name`} hidden />
        <Form.Item name={`flag`} hidden />
        <Form.Item name={`inStock`} hidden />
        <Form.Item name={`providerPrice`} hidden />
        <Form.Item name={`margin`} hidden />
        <Form.Item name={`nudosPriceUsd`} hidden />
        <Form.Item name={`nudosPrice`} hidden />
        <Form.Item name={`nudosPriceUsdWithTaxes`} hidden />
        <Form.Item name={`nudosPriceWithTaxes`} hidden />
        <Form.Item name={`providerId`} hidden />
        <Form.Item name={`taxRate`} hidden />
        <Form.Item name={`availableProviders`} hidden />
        <Form.Item name={`newCountryIsActive`} hidden />
        <Form.Item name={`newCountryIsQuoteProduct`} hidden />
      </div>
      <div className="closeFormContainer">
        {isOnlyDevsAutomates && isCreatingProduct && (
          <div className="statusTabsContainer">
            <span className="statusTabsTitle">Estado de la herramienta en tienda</span>
            <ProductStatusTabs currentStatus={productStatus} onStatusChange={handleStatusChange} />
          </div>
        )}
        {isOnlyDevsAutomates && !!productDataDetail?.productId && (
          <div className="buttonsContainer">
            <NudosButton
              buttonText="Prender solo para cotizar"
              customClassName="customButton"
              isButtonDisabled={!isQuoteAvailable}
              handleClick={() => handleSaveCountry(true)}
            />
            <NudosButton
              buttonText="Prender en tienda"
              customClassName="customButton"
              isButtonDisabled={!isStoreAvailable}
              handleClick={() => handleSaveCountry(false)}
            />
          </div>
        )}
        <div className="cursor-pointer">
          <CloseNewCountryIcon
            onClick={() => {
              if (setShowPricingForm) {
                setShowPricingForm(false);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PricingForm;
